import { useEffect, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { toast } from "@/components/ui/sonner";
import { fetchPaged } from "@/api/paged";
import { cn } from "@/lib/utils";
import { criarTela, type Tela } from "./api";

const TELAS_PATH = "instituicao/Telas";

interface TelaLookupProps {
  value: Tela | null;
  onChange: (value: Tela | null) => void;
  disabled?: boolean;
  id?: string;
}

// Lookup dedicado só pra esta rota — não generalizado em cima do
// <EntityLookup> compartilhado porque o contrato de sp-lookup (Telas) é
// diferente o bastante pra não valer a pena forçar no mesmo componente:
// campos telaId/tela em vez de id/descricao, o filtro de busca é
// `textoLivre` (não `descricao`), e há suporte a criar um item novo direto
// da busca (addNew() do legado, `app/directives/lookup/*`). Se aparecer uma
// segunda entidade usando sp-lookup, generalizar então — não antes.
export function TelaLookup({ value, onChange, disabled, id }: TelaLookupProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [confirmNovaOpen, setConfirmNovaOpen] = useState(false);
  const qc = useQueryClient();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 500);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ["tela-lookup", debounced],
    queryFn: () =>
      fetchPaged<Tela, { textoLivre?: string }>(TELAS_PATH, {
        page: 0,
        size: 10,
        filtro: debounced ? { textoLivre: debounced } : {},
      }),
    enabled: open,
  });

  const criar = useMutation({
    mutationFn: criarTela,
    onSuccess: (nova) => {
      toast.success("Tela cadastrada.");
      qc.invalidateQueries({ queryKey: ["tela-lookup"] });
      selecionar(nova);
    },
  });

  function selecionar(item: Tela) {
    onChange(item);
    setOpen(false);
    setSearch("");
  }

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setSearch("");
  }

  function handleKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (disabled) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setOpen(true);
    }
  }

  function limpar(e: MouseEvent) {
    e.stopPropagation();
    onChange(null);
  }

  return (
    <div className="space-y-1">
      <Label htmlFor={id} onClick={() => !disabled && setOpen(true)} className={disabled ? undefined : "cursor-pointer"}>
        Tela
      </Label>
      <div
        id={id}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-haspopup="dialog"
        onClick={() => !disabled && setOpen(true)}
        onKeyDown={handleKeyDown}
        className={cn(
          "flex h-10 items-center gap-2 rounded-md border border-input bg-background px-3 text-sm ring-offset-background transition-colors",
          disabled
            ? "cursor-not-allowed opacity-50"
            : "cursor-pointer hover:border-primary/50 hover:bg-accent/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        )}
      >
        <span className={cn("flex-1 truncate", !value?.tela && "text-muted-foreground")}>
          {value?.tela ?? "Nenhuma selecionada"}
        </span>
        {value && !disabled && (
          <button
            type="button"
            onClick={limpar}
            aria-label="Limpar Tela"
            className="shrink-0 rounded-sm text-muted-foreground hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        )}
        <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
      </div>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="overflow-hidden p-0">
          <DialogHeader className="border-b px-4 pb-0 pt-4">
            <DialogTitle>Pesquisa de Telas</DialogTitle>
          </DialogHeader>
          <Command shouldFilter={false}>
            <div className="flex items-center gap-2 border-b px-3">
              <CommandInput value={search} onValueChange={setSearch} placeholder="Pesquisar Telas..." className="border-0" />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                disabled={!search.trim()}
                onClick={() => setConfirmNovaOpen(true)}
                title="Cadastrar novo"
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            <CommandList>
              {isLoading && <div className="p-4 text-sm text-muted-foreground">Buscando...</div>}
              {!isLoading && <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>}
              <CommandGroup>
                {(data?.content ?? []).map((item) => (
                  <CommandItem key={item.telaId} value={String(item.telaId)} onSelect={() => selecionar(item)}>
                    {item.telaId} — {item.tela}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmNovaOpen}
        onOpenChange={setConfirmNovaOpen}
        title="Cadastrar nova tela?"
        description={`Você deseja cadastrar um novo item chamado "${search.trim()}"?`}
        confirmLabel="Cadastrar"
        onConfirm={() => criar.mutate(search.trim())}
      />
    </div>
  );
}
